import { createHttpClient, type CreateHttpClientOptions } from './http';
import { serviceUrls } from '../config/serviceUrls';

/**
 * AI Tutor mounts `/admin/prompt-library` and `/api/...` at the **root** of the service.
 * A base like `http://example.com/api` would produce `/api/admin/...` → **404** if not normalized.
 */
export function normalizeTutorBaseUrl(raw: string): string {
  let u = raw.trim().replace(/\/+$/, '');
  if (u.endsWith('/api')) {
    u = u.slice(0, -4);
  }
  return u;
}

/** Used for ai-tutor admin + public editor APIs */
export const TUTOR_HTTP_TIMEOUT_MS = 30_000;

/**
 * Returns a single gateway URL to use for all AI Tutor HTTP calls.
 *
 * - All AI Tutor requests must go through the configured gateway only.
 * - If `VITE_AI_TUTOR_BASE_URL` is set, it will be normalized and used as the gateway URL.
 * - No direct-service fallback (localhost:8007) is attempted.
 * - In local dev, direct :8007 is redirected to the gateway so CORS + JWT forwarding work.
 */
export function resolveStudentTutorHttpBase(): string {
  const configured = import.meta.env.VITE_AI_TUTOR_BASE_URL?.trim();
  const gatewayUrl = normalizeTutorBaseUrl(serviceUrls.gateway);
  if (!configured) return gatewayUrl;
  const normalized = normalizeTutorBaseUrl(configured);
  if (
    import.meta.env.DEV &&
    /^https?:\/\/(localhost|127\.0\.0\.1):8007\/?$/i.test(normalized)
  ) {
    console.warn(
      '[tutor] VITE_AI_TUTOR_BASE_URL points at ai-tutor :8007; using gateway instead (CORS + auth). Set VITE_AI_TUTOR_BASE_URL=http://localhost:8080 or unset it.'
    );
    return gatewayUrl;
  }
  return normalized;
}

export function getTutorHttpBaseCandidates(): string[] {
  return [resolveStudentTutorHttpBase()];
}

export function createTutorHttpClients(options?: CreateHttpClientOptions): ReturnType<typeof createHttpClient>[] {
  return getTutorHttpBaseCandidates().map((base) =>
    createHttpClient(base, { ...options, timeoutMs: options?.timeoutMs ?? TUTOR_HTTP_TIMEOUT_MS })
  );
}
